import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const HealthConsultationPage = () => {
  const handleHomeClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const consultationTopics = [
    {
      icon: 'ri-heart-pulse-line',
      title: '体のだるさ・疲れが取れない',
      color: 'bg-rose-50 text-rose-700'
    },
    {
      icon: 'ri-temp-cold-line',
      title: '冷え・肩こり・頭痛・不眠',
      color: 'bg-blue-50 text-blue-700'
    },
    {
      icon: 'ri-hand-sanitizer-line',
      title: '皮膚のかゆみや湿疹',
      color: 'bg-amber-50 text-amber-700'
    },
    {
      icon: 'ri-restaurant-line',
      title: '食欲・胃腸の不調',
      color: 'bg-green-50 text-green-700'
    },
    {
      icon: 'ri-mental-health-line',
      title: 'ストレス・自律神経の乱れ',
      color: 'bg-purple-50 text-purple-700'
    },
    {
      icon: 'ri-capsule-line',
      title: 'サプリメントや健康食品の選び方',
      color: 'bg-teal-50 text-teal-700'
    }
  ];

  const features = [
    {
      icon: 'ri-user-heart-line',
      title: '丁寧なヒアリング',
      description: '薬剤師が生活習慣・食事・お薬の使い方などを一緒に考えます'
    },
    {
      icon: 'ri-medicine-bottle-line',
      title: '最適な提案',
      description: '医薬品・漢方・栄養補助食品などから、体質や症状に合った方法を提案'
    },
    {
      icon: 'ri-shield-check-line',
      title: '守秘義務の徹底',
      description: '相談内容はすべて守秘義務をもって対応しますのでご安心ください'
    }
  ];

  const consultationSteps = [
    {
      number: '1',
      title: 'ご来店またはお電話',
      description: 'お気軽にご相談ください'
    },
    {
      number: '2',
      title: 'お話を伺う',
      description: '現在のお薬や体調を確認'
    },
    {
      number: '3',
      title: '適切なアドバイス',
      description: '薬剤師が商品を提案'
    },
    {
      number: '4',
      title: '継続的なフォロー',
      description: '体調の変化をサポート'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "一心堂薬局 健康相談",
    "description": "地域のかかりつけ薬局として、体調や生活習慣のご相談を承っています。薬剤師が丁寧にお話を伺い、最適なアドバイスを提供します。",
    "url": `${import.meta.env.VITE_SITE_URL}/health-consultation`,
    "telephone": "0479-70-6065",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "多古1038-1",
      "addressLocality": "多古町",
      "addressRegion": "千葉県香取郡",
      "postalCode": "289-2241",
      "addressCountry": "JP"
    },
    "medicalSpecialty": "健康相談・薬剤師相談",
    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "健康相談",
        "description": "体調や生活習慣に関する相談"
      },
      {
        "@type": "MedicalProcedure",
        "name": "サプリメント相談",
        "description": "栄養補助食品の選び方相談"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <SEOHead
        title="からだとこころの健康を支える - 一心堂薬局の健康相談"
        description="一心堂薬局では、お薬のことだけでなく、毎日の体調や生活習慣のご相談も承っています。ちょっとした不調や気になる症状も、どうぞお気軽にご相談ください。"
        keywords="健康相談,薬剤師相談,生活習慣改善,未病ケア,一心堂薬局,千葉県香取郡,多古町,体質改善,サプリメント相談,地域のかかりつけ薬局"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL}/health-consultation`}
        ogTitle="からだとこころの健康を支える - 一心堂薬局の健康相談"
        ogDescription="地域のかかりつけ薬局として、体調や生活習慣のご相談を承っています。薬剤師が丁寧にお話を伺い、最適なアドバイスを提供します。"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" onClick={handleHomeClick} className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <i className="ri-leaf-fill text-white text-sm"></i>
              </div>
              <span className="font-bold text-green-800" style={{fontFamily: 'Pacifico, serif'}}>
                相談できる薬局 一心堂薬局
              </span>
            </Link>
            <Link to="/" onClick={handleHomeClick} className="w-8 h-8 flex items-center justify-center">
              <i className="ri-close-line text-green-800 text-xl"></i>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <img 
              src="https://public.readdy.ai/ai/img_res/ed789cc66e5663d628eba13a63bd35ef.jpg"
              alt="健康相談のイメージ"
              className="w-full h-48 object-cover rounded-2xl shadow-lg"
            />
          </div>
          <h1 className="text-2xl font-bold text-green-800 mb-3">
            からだとこころの健康を支える
          </h1>
          <p className="text-green-700 text-lg mb-4">
            一心堂薬局の健康相談
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            一心堂薬局では、お薬のことだけでなく、<br />
            毎日の体調や生活習慣のご相談も承っています。<br />
            ちょっとした不調や気になる症状も、<br />
            どうぞお気軽にご相談ください。
          </p>
        </div>
      </div>

      {/* Consultation Topics */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            相談できる内容の例
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {consultationTopics.map((topic, index) => (
              <Link 
                key={index} 
                to={index === 0 ? '/fatigue' : '#'}
                className={`${topic.color} rounded-2xl p-4 text-center block hover:opacity-80 transition-opacity`}
              >
                <div className="w-12 h-12 bg-white/80 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className={`${topic.icon} text-2xl`}></i>
                </div>
                <p className="text-sm font-medium leading-snug">{topic.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="px-4 py-8 bg-green-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            相談の特徴
          </h2>
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-5 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-green-600 text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-800 mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Consultation Flow */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            相談の流れ
          </h2>
          <div className="space-y-4">
            {consultationSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                    {step.number}
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="font-semibold text-green-800 mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
                {index < consultationSteps.length - 1 && (
                  <div className="absolute left-6 top-12 w-0.5 h-8 bg-green-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Community Pharmacy */}
      <div className="px-4 py-8 bg-green-50">
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-br from-green-100 to-green-50 rounded-2xl p-6 border-l-4 border-green-600">
            <h2 className="text-xl font-bold text-green-800 mb-4 text-center">
              地域のかかりつけ薬局として
            </h2>
            <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
              <p>
                一心堂薬局は、病院や介護、在宅支援などとも連携しながら、地域全体の健康づくりに取り組んでいます。
              </p>
              <p>
                ちょっとしたことでも「話してよかった」と思える薬局を目指しています。
              </p>
            </div>
            <div className="mt-6 text-center">
              <div className="inline-flex items-center space-x-2 bg-white/80 px-4 py-2 rounded-full">
                <i className="ri-heart-line text-green-600"></i>
                <span className="text-sm font-medium text-green-800">健康を一緒に考える場所</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warm Atmosphere Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <img 
            src="https://readdy.ai/api/search-image?query=Warm%20Japanese%20pharmacy%20interior%20with%20pharmacist%20and%20customers%2C%20friendly%20atmosphere%2C%20natural%20wood%20tones%2C%20soft%20lighting%2C%20community%20healthcare%20setting%2C%20realistic%20photography%20style%2C%20welcoming%20environment&width=350&height=200&seq=health-consult2&orientation=landscape"
            alt="薬局の温かい雰囲気"
            className="w-full h-48 object-cover rounded-2xl shadow-lg"
          />
          <div className="mt-6 text-center">
            <a 
              href="tel:0479-70-6065"
              className="inline-flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <i className="ri-phone-line text-lg"></i>
              <span>電話</span>
            </a>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="px-4 py-8 bg-green-50">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
            <h3 className="text-xl font-bold text-green-800 mb-4">
              あなたの「なんとなく気になる」を、<br />
              ぜひご相談ください
            </h3>
            <p className="text-sm text-gray-700 mb-6 leading-relaxed">
              一心堂薬局は、からだとこころの健康をサポートいたします。
            </p>
            <div className="mt-6">
              <Link 
                to="/"
                onClick={handleHomeClick}
                className="inline-block bg-blue-600 text-white py-3 px-8 rounded-full font-medium text-sm"
              >
                <i className="ri-home-line mr-2"></i>
                ホームへ
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 z-50">
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-5 py-2">
            <Link to="/" onClick={handleHomeClick} className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-home-5-line text-lg"></i>
              <span className="text-xs mt-1">ホーム</span>
            </Link>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-medicine-bottle-line text-lg"></i>
              <span className="text-xs mt-1">調剤</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-leaf-line text-lg"></i>
              <span className="text-xs mt-1">漢方相談</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-blue-600">
              <i className="ri-heart-pulse-fill text-lg"></i>
              <span className="text-xs mt-1">健康相談</span>
            </button>
            <a href="tel:0479-70-6065" className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-phone-line text-lg"></i>
              <span className="text-xs mt-1">お問合せ</span>
            </a>
          </div>
        </div>
      </nav>

      <div className="h-20"></div>
    </div>
  );
};

export default HealthConsultationPage;
